/**
 * Pure helpers for consolidating multi-source signals into Radar work threads.
 * Kept small (CC ≤ 4) so CRAP stays under the monorepo gate.
 */

import type { AgentItem, AttentionItem, NoteItem, ReminderItem, Tone } from './types';
import { assignRadarLane } from './radarLanes';
import type {
  BriefingBucketId,
  WorkThread,
  WorkThreadAction,
  WorkThreadSourceStatus,
} from './radarThreads';

export type TicketGroup = {
  jira: AttentionItem[];
  github: AttentionItem[];
  slack: AttentionItem[];
  gmail: AttentionItem[];
  agents: AgentItem[];
  otherItems: AttentionItem[];
  notes: NoteItem[];
  reminders: ReminderItem[];
  /** Jira key discussed in Slack when the issue is not in the inbox. */
  impliedJiraKey?: string;
};

export function emptyTicketGroup(): TicketGroup {
  return {
    jira: [],
    github: [],
    slack: [],
    gmail: [],
    agents: [],
    otherItems: [],
    notes: [],
    reminders: [],
  };
}

const SOURCE_SETTERS: Record<string, (group: TicketGroup, item: AttentionItem) => void> = {
  jira: (group, item) => {
    group.jira.push(item);
  },
  github: (group, item) => {
    group.github.push(item);
  },
  slack: (group, item) => {
    group.slack.push(item);
  },
  gmail: (group, item) => {
    group.gmail.push(item);
  },
};

export function assignItemToGroup(group: TicketGroup, item: AttentionItem): void {
  const setter = SOURCE_SETTERS[item.source];
  if (setter) {
    setter(group, item);
    return;
  }
  group.otherItems.push(item);
}

export function assignAgentToGroup(group: TicketGroup, agent: AgentItem): void {
  group.agents.push(agent);
}

function jiraCriteriaDetail(jira: AttentionItem): string {
  const criteria = jira.criteria ?? [];
  if (criteria.length === 0) return jira.workflow || 'En progreso';
  const doneCount = criteria.filter((c) => c.status === 'done').length;
  return `${doneCount}/${criteria.length} CA listos`;
}

function withCount(detail: string, count: number): string {
  if (count <= 1) return detail;
  return `${detail} · +${count - 1}`;
}

function firstAiBrief(items: AttentionItem[]): string | undefined {
  for (const item of items) {
    const brief = item.ai?.trim();
    if (brief) return brief;
  }
  return undefined;
}

export function jiraSourceAndAction(jiraItems: AttentionItem[]): {
  source: WorkThreadSourceStatus;
  action: WorkThreadAction;
} {
  const jira = jiraItems[0]!;
  return {
    source: {
      source: 'jira',
      label: 'Jira',
      detail: withCount(jiraCriteriaDetail(jira), jiraItems.length),
      tone: jira.st === 'blocked' ? 'bad' : 'accent',
      aiBrief: firstAiBrief(jiraItems),
    },
    action: {
      id: `act-jira-${jira.id}`,
      label: 'Abrir en Jira',
      source: 'jira',
      variant: 'tertiary',
      targetId: jira.id,
      targetView: 'jira',
    },
  };
}

export function impliedJiraSourceAndAction(ticketKey: string): {
  source: WorkThreadSourceStatus;
  action: WorkThreadAction;
} {
  return {
    source: {
      source: 'jira',
      label: 'Jira',
      detail: ticketKey,
      tone: 'accent',
      aiBrief: `El hilo discute ${ticketKey}, aunque el issue no está en el inbox de Jira.`,
    },
    action: {
      id: `act-jira-${ticketKey}`,
      label: 'Abrir en Jira',
      source: 'jira',
      variant: 'tertiary',
      targetId: ticketKey,
      targetView: 'jira',
    },
  };
}

export function githubSourceAndAction(githubItems: AttentionItem[]): {
  source: WorkThreadSourceStatus;
  action: WorkThreadAction;
} {
  const github = githubItems[0]!;
  const commentsCount = github.comments?.length ?? 0;
  return {
    source: {
      source: 'github',
      label: 'GitHub',
      detail: withCount(`${github.primary} · ${commentsCount} comentarios`, githubItems.length),
      tone: github.st === 'you' ? 'warn' : 'ok',
      aiBrief: firstAiBrief(githubItems),
    },
    action: {
      id: `act-gh-${github.id}`,
      label: `Ver PR ${github.primary}`,
      source: 'github',
      variant: 'secondary',
      targetId: github.id,
      targetView: 'github',
    },
  };
}

export function slackSourceAndAction(slackItems: AttentionItem[]): {
  source: WorkThreadSourceStatus;
  action: WorkThreadAction;
} {
  const slack = slackItems[0]!;
  const waitingOnUser = slack.st === 'you';
  const messageCount = slack.messages?.length ?? 1;
  return {
    source: {
      source: 'slack',
      label: 'Slack',
      detail: withCount(`${slack.primary} · ${messageCount} mensajes`, slackItems.length),
      tone: waitingOnUser ? 'warn' : 'ok',
      aiBrief: firstAiBrief(slackItems),
    },
    action: {
      id: `act-slack-${slack.id}`,
      label: waitingOnUser ? 'Responder en Slack' : 'Revisar en Slack',
      source: 'slack',
      variant: waitingOnUser ? 'primary' : 'secondary',
      targetId: slack.id,
      targetView: 'slack',
    },
  };
}

export function gmailSourceStatus(gmailItems: AttentionItem[]): WorkThreadSourceStatus {
  const gmail = gmailItems[0]!;
  return {
    source: 'gmail',
    label: 'Gmail',
    detail: withCount(
      gmail.title || 'rotación de claves para ports_dashboard_api',
      gmailItems.length,
    ),
    tone: 'warn',
    aiBrief: firstAiBrief(gmailItems),
  };
}

export function gmailSourceAndAction(gmailItems: AttentionItem[]): {
  source: WorkThreadSourceStatus;
  action: WorkThreadAction;
} {
  const gmail = gmailItems[0]!;
  return {
    source: gmailSourceStatus(gmailItems),
    action: {
      id: `act-gmail-${gmail.id}`,
      label: gmail.unread ? 'Responder correo' : 'Ver correo',
      source: 'gmail',
      variant: gmail.unread ? 'secondary' : 'tertiary',
      targetId: gmail.id,
      targetView: 'gmail',
    },
  };
}

export function agentSourceStatus(agents: AgentItem[]): WorkThreadSourceStatus {
  const agent = agents[0]!;
  const last = agent.last?.trim();
  return {
    source: 'agents',
    label: agent.name ? `Agente ${agent.name}` : 'Agente',
    detail: withCount(`${agent.name} · ${agent.elapsed || '12m'}`, agents.length),
    tone: agent.running ? 'ok' : 'warn',
    aiBrief: last ? `Última actividad: ${last}` : undefined,
  };
}

function pushPair(
  sources: WorkThreadSourceStatus[],
  actions: WorkThreadAction[],
  part: { source: WorkThreadSourceStatus; action: WorkThreadAction },
  unshiftAction: boolean,
): void {
  sources.push(part.source);
  if (unshiftAction) actions.unshift(part.action);
  else actions.push(part.action);
}

export function collectGroupSourcesAndActions(group: TicketGroup): {
  sources: WorkThreadSourceStatus[];
  actions: WorkThreadAction[];
} {
  const sources: WorkThreadSourceStatus[] = [];
  const actions: WorkThreadAction[] = [];
  if (group.jira.length > 0) pushPair(sources, actions, jiraSourceAndAction(group.jira), false);
  else if (group.impliedJiraKey) {
    pushPair(sources, actions, impliedJiraSourceAndAction(group.impliedJiraKey), false);
  }
  if (group.github.length > 0)
    pushPair(sources, actions, githubSourceAndAction(group.github), false);
  if (group.slack.length > 0) pushPair(sources, actions, slackSourceAndAction(group.slack), true);
  if (group.gmail.length > 0) pushPair(sources, actions, gmailSourceAndAction(group.gmail), false);
  appendOptionalSources(group, sources);
  return { sources, actions };
}

function appendOptionalSources(group: TicketGroup, sources: WorkThreadSourceStatus[]): void {
  if (group.agents.length > 0) sources.push(agentSourceStatus(group.agents));
  if (group.notes.length > 0) {
    sources.push({
      source: 'notes',
      label: 'Notas',
      detail: withCount(`${group.notes.length} nota(s) enlazada(s)`, group.notes.length),
      tone: 'flat',
      aiBrief: `Hay ${group.notes.length} nota(s) vinculadas; la más reciente apunta a seguimiento local del contexto.`,
    });
  }
  if (group.reminders.length > 0) {
    const reminder = group.reminders[0]!;
    sources.push({
      source: 'reminders',
      label: 'Recordatorios',
      detail: withCount(
        reminder.time ? `Próximo a las ${reminder.time}` : `${group.reminders.length} activo(s)`,
        group.reminders.length,
      ),
      tone: 'flat',
      aiBrief: `Hay ${group.reminders.length} recordatorio(s) activos ligados a este contexto.`,
    });
  }
}

function groupAttentionItems(group: TicketGroup): AttentionItem[] {
  return [...group.jira, ...group.github, ...group.slack, ...group.gmail, ...group.otherItems];
}

const LANE_TO_BRIEFING_BUCKET: Record<string, BriefingBucketId> = {
  'waiting-you': 'require-attention',
  now: 'watching',
  stuck: 'blockers',
  ready: 'under-control',
};

export function briefingBucketsForGroup(group: TicketGroup): BriefingBucketId[] {
  const seen = new Set<BriefingBucketId>();
  for (const item of groupAttentionItems(group)) {
    const lane = assignRadarLane(item);
    const bucket = lane ? LANE_TO_BRIEFING_BUCKET[lane] : undefined;
    if (bucket) seen.add(bucket);
  }
  for (const agent of group.agents) {
    seen.add(agent.running ? 'watching' : 'require-attention');
  }
  return [...seen];
}

function anyStatus(statuses: Array<string | undefined>, expected: string): boolean {
  return statuses.some((st) => st === expected);
}

function groupStatuses(group: TicketGroup): Array<string | undefined> {
  return [
    ...group.slack.map((item) => item.st),
    ...group.github.map((item) => item.st),
    ...group.jira.map((item) => item.st),
    ...group.gmail.map((item) => item.st),
  ];
}

type ThreadStatusFields = Pick<
  WorkThread,
  'status' | 'statusLabel' | 'statusTone' | 'priorityRank'
>;

export function resolveThreadStatus(group: TicketGroup): ThreadStatusFields {
  if (anyStatus(groupStatuses(group), 'you')) {
    return {
      status: 'you',
      statusLabel: 'Requiere tu respuesta',
      statusTone: 'warn',
      priorityRank: 1,
    };
  }
  if (
    anyStatus(
      [...group.jira, ...group.github].map((item) => item.st),
      'blocked',
    )
  ) {
    return { status: 'blocked', statusLabel: 'Atascado', statusTone: 'bad', priorityRank: 2 };
  }
  if (
    anyStatus(
      [...group.jira, ...group.github].map((item) => item.st),
      'ready',
    )
  ) {
    return { status: 'ready', statusLabel: 'Listo para cerrar', statusTone: 'ok', priorityRank: 4 };
  }
  return { status: 'them', statusLabel: 'En curso', statusTone: 'accent' as Tone, priorityRank: 3 };
}

function firstDefined<T>(values: Array<T | undefined>): T | undefined {
  return values.find((value) => value !== undefined);
}

export function primaryGroupItem(group: TicketGroup): AttentionItem | undefined {
  return firstDefined([
    group.jira[0],
    group.github[0],
    group.slack[0],
    group.gmail[0],
    group.otherItems[0],
  ]);
}

function threadTitle(
  primaryItem: AttentionItem | undefined,
  agents: AgentItem[],
  key: string,
): string {
  return firstDefined([primaryItem?.title, agents[0]?.ticket]) ?? key;
}

function threadDescription(
  primaryItem: AttentionItem | undefined,
  jira?: AttentionItem,
): string | undefined {
  return firstDefined([primaryItem?.desc, jira?.desc]);
}

function threadUpdatedText(primaryItem: AttentionItem | undefined): string {
  if (primaryItem?.age) return `Actualizado ${primaryItem.age}`;
  return 'Actualizado hace 2h';
}

function threadAiDiagnostic(group: TicketGroup, primaryItem: AttentionItem | undefined): string {
  return (
    firstDefined([group.slack[0]?.ai, primaryItem?.ai]) ??
    'Señales sincronizadas entre plataformas.'
  );
}

function threadNextStep(group: TicketGroup, primaryItem: AttentionItem | undefined): string {
  return (
    firstDefined([group.slack[0]?.next, primaryItem?.next]) ??
    'Continuar seguimiento en los workspaces.'
  );
}

export function buildThreadFromGroup(
  group: TicketGroup,
  key: string,
  options: { title?: string; isManual?: boolean } = {},
): WorkThread | null {
  const primaryItem = primaryGroupItem(group);
  const isManual = Boolean(options.isManual);
  if (!primaryItem && group.agents.length === 0 && !isManual) {
    if (group.notes.length === 0 && group.reminders.length === 0) return null;
  }

  const { sources, actions } = collectGroupSourcesAndActions(group);
  const impliedJiraCount = group.jira.length === 0 && group.impliedJiraKey ? 1 : 0;
  const memberCount =
    group.jira.length +
    group.github.length +
    group.slack.length +
    group.gmail.length +
    group.otherItems.length +
    group.agents.length +
    group.notes.length +
    group.reminders.length +
    impliedJiraCount +
    (isManual ? 1 : 0);

  const hasUnread =
    [...group.slack, ...group.gmail, ...group.otherItems].some((item) => item.unread === true) ||
    anyStatus(groupStatuses(group), 'you');

  return {
    id: key,
    key,
    title: options.title || threadTitle(primaryItem, group.agents, key),
    description: threadDescription(primaryItem, group.jira[0]),
    ...resolveThreadStatus(group),
    updatedText: threadUpdatedText(primaryItem),
    updatedAt: primaryItem?.updatedAt || new Date().toISOString(),
    sources,
    aiDiagnostic: threadAiDiagnostic(group, primaryItem),
    nextStep: threadNextStep(group, primaryItem),
    actions,
    isManual,
    memberCount,
    hasUnread,
    briefingBuckets: briefingBucketsForGroup(group),
  };
}
